/**
 * The project workspace: header, the section rail, and the loaded project.
 *
 * The project is fetched once here and handed to the sections through the
 * router outlet context, so switching sections does not refetch it and they
 * cannot disagree about what they are showing.
 *
 * The rail lists **all six** sections, from `PROJECT_SECTIONS` - the same
 * definition the sidebar renders. It used to carry its own array of three,
 * which is how the header came to describe half the product while the sidebar
 * described all of it.
 */

import { useCallback } from "react";
import { Link, Outlet, useOutletContext, useParams } from "react-router-dom";
import { getProject } from "@/api/client";
import type { Project } from "@/api/types";
import { Loading, StatusBadge } from "@/components/ui";
import { ActiveWorkStrip } from "./ActiveWorkStrip";
import { ErrorState } from "@/components/ErrorState";
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_TONES,
  SUBJECT_TYPE_TERMS,
} from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import { ProjectSectionRail } from "./ProjectSectionRail";
import "./projects.css";

export interface ProjectContext {
  project: Project;
  reloadProject: () => void;
}

/** Typed access to the project for the three tabs below this route. */
export const useProjectContext = (): ProjectContext => useOutletContext<ProjectContext>();

export function ProjectScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const fetcher = useCallback(
    (signal: AbortSignal) => getProject(projectId ?? "", { signal }),
    [projectId],
  );
  const { state, reload } = useAsync(fetcher);

  if (state.status === "error") {
    return (
      <div className="stack">
        <Link to="/projeler" className="muted">
          ← Projeler
        </Link>
        <ErrorState error={state.error} onRetry={reload} />
      </div>
    );
  }

  if (state.data === null) return <Loading label="Proje yükleniyor" />;

  const project = state.data;
  const subject = SUBJECT_TYPE_TERMS[project.subject_type];
  const context: ProjectContext = { project, reloadProject: reload };

  return (
    <div className="stack">
      <div className="stack-tight">
        <Link to="/projeler" className="muted">
          ← Projeler
        </Link>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "start" }}>
          <h1 style={{ overflowWrap: "anywhere" }}>{project.name}</h1>
          <StatusBadge tone={PROJECT_STATUS_TONES[project.status]}>
            {PROJECT_STATUS_LABELS[project.status]}
          </StatusBadge>
        </div>
        <p className="muted">
          {subject.label} · Oluşturulma {formatDateTime(project.created_at)}
        </p>
      </div>

      {/* Above the rail, so it is present on every one of the six
          sections rather than only the one that started the work. */}
      <ActiveWorkStrip projectId={project.id} />

      <ProjectSectionRail projectId={project.id} />

      <Outlet context={context} />
    </div>
  );
}
